/**
 * Overview View Minimal - Main Component
 * ChatGPT-style dashboard with AI assistant
 * NOW WITH SAGA-STYLE PDF SIDE PANEL 🎯
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Camera, Sparkles, ArrowRight, Calendar, Clock, Plus, ChevronDown, Mic, Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { API_URL } from '../../../utils/api';
import { useTranslation } from '../../../context/LanguageContext';
import { useTimeTracking } from '../../../contexts/TimeTrackingContext';
import CreateCaseModal from '../../inbox/triage/CreateCaseModal';
import VoiceMode from '../../ai-workspace/VoiceMode';
import { PDFSidePanel } from '../../ai/PDFSidePanel';
import { aiApi, activityApi } from '../../../services';

import { useChat, useSearch, useTimeline } from './hooks';
import { SearchDropdown, ChatMessage, TimelineItem, CaseItem, RecommendationsCard, ActionPanel } from './components';
import type { UserData, DashboardData, Citation } from './types';

interface OverviewViewMinimalProps {
  onNavigate?: (path: string) => void;
  user?: UserData;
  dashboardData?: DashboardData;
  navigate?: (path: string) => void;
  onUserUpdate?: () => void;
}

const OverviewViewMinimal: React.FC<OverviewViewMinimalProps> = ({ 
  onNavigate, 
  user, 
  dashboardData: propDashboardData, 
  navigate: propNavigate, 
  onUserUpdate 
}) => {
  const { language } = useTranslation();
  const routerNavigate = useNavigate();
  const navigate = propNavigate || routerNavigate;
  const t = (en: string, no: string) => language === 'en' ? en : no;
  
  // Time tracking - set to "general" when on dashboard
  const { startContextTracking, currentContext, isTracking } = useTimeTracking();
  
  // Start general tracking when viewing dashboard (not case-specific)
  useEffect(() => {
    // Only switch to general if currently tracking something other than a case
    // This prevents overriding case tracking when briefly visiting dashboard
    if (isTracking && currentContext !== 'general' && currentContext !== 'dashboard') {
      // Don't override case tracking
      return;
    }
    // If tracking inbox/ai_workspace context (not case), switch to general
    if (currentContext === 'inbox' || currentContext === 'ai_workspace') {
      startContextTracking('general');
    }
  }, [currentContext, isTracking, startContextTracking]);
  
  // Refs
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // State
  const [uploading, setUploading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showNewCaseModal, setShowNewCaseModal] = useState(false);
  const [isCreatingCase, setIsCreatingCase] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [streamingContext, setStreamingContext] = useState('');
  const [streamingPhase, setStreamingPhase] = useState<'greeting' | 'context' | 'timeline' | 'done'>('greeting');
  const [isStreaming, setIsStreaming] = useState(false);
  const [voiceModeOpen, setVoiceModeOpen] = useState(false);
  
  // 🎯 Saga-style document side panel state
  const [documentSidePanel, setDocumentSidePanel] = useState<{ isOpen: boolean; citation?: Citation }>({
    isOpen: false,
    citation: undefined
  });
  
  const allCases = propDashboardData?.allCases || propDashboardData?.cases || [];
  const events = propDashboardData?.events || [];
  
  // Parse markdown helper
  const parseMarkdown = (text: string) => {
    if (!text) return '';
    return text
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/`(.*?)`/g, '$1')
      .trim();
  };
  
  // Custom hooks
  const {
    chatInput,
    setChatInput,
    chatHistory,
    isTyping,
    isStreaming: chatIsStreaming,
    briefShown,
    setBriefShown,
    briefCollapsed,
    setBriefCollapsed,
    briefShownRef,
    handleSubmit,
    addBriefMessage,
  } = useChat({ user, allCases, language, t, parseMarkdown });
  
  const {
    searchQuery,
    setSearchQuery,
    showSearchDropdown,
    setShowSearchDropdown,
    searchSelectedIndex,
    setSearchSelectedIndex,
    searchInputRef,
    searchDropdownRef,
    searchResults,
    handleSearchKeyDown,
    handleSearchResultClick,
  } = useSearch({ allCases, language, t, onNewCase: () => setShowNewCaseModal(true) });
  
  const { todayTimeline, priorityCases } = useTimeline({ events, allCases, currentTime, language, t });

  // Update time every minute
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, streamingText]);

  // Auto scroll when chat updates
  useEffect(() => {
    if (chatHistory.length > 1) {
      setTimeout(() => {
        chatEndRef.current?.scrollTo({ top: chatEndRef.current.scrollHeight, behavior: 'smooth' });
      }, 100);
    }
  }, [chatHistory.length]);

  // Generate contextual greeting
  const generateContextualGreeting = useCallback(() => {
    const now = currentTime;
    const hour = now.getHours();
    const dayOfWeek = now.getDay();
    const firstName = user?.name?.split(' ')[0] || '';
    
    let timeGreeting = hour < 12 
      ? t('Good morning', 'God morgen')
      : hour < 17 
        ? t('Good afternoon', 'God ettermiddag')
        : t('Good evening', 'God kveld');
    
    const activeCases = allCases?.filter((c: any) => c.status !== 'COMPLETED') || [];
    const urgentCases = activeCases.filter((c: any) => c.priority === 'CRITICAL' || c.priority === 'HIGH');
    const todayDeadlines = activeCases.filter((c: any) => {
      if (!c.deadline) return false;
      return new Date(c.deadline).toDateString() === now.toDateString();
    });
    const todayMeetings = todayTimeline.filter(item => item.type === 'meeting');
    
    let greeting = `${timeGreeting}${firstName ? ', ' + firstName : ''}.`;
    let context = '';
    
    if (dayOfWeek === 1) {
      context = t('Fresh start to the week.', 'Ny uke, frisk start.');
    } else if (dayOfWeek === 5) {
      context = t('Almost weekend!', 'Nesten helg!');
    }
    
    if (todayDeadlines.length > 0) {
      context = t(
        `${todayDeadlines.length} deadline${todayDeadlines.length > 1 ? 's' : ''} today.`,
        `${todayDeadlines.length} frist${todayDeadlines.length > 1 ? 'er' : ''} i dag.`
      );
    } else if (urgentCases.length > 2) {
      context = t(
        `Busy day ahead — ${urgentCases.length} high priority cases.`,
        `Travelt i dag — ${urgentCases.length} saker med høy prioritet.`
      );
    } else if (activeCases.length === 0) {
      context = t('All clear. Ready for new work!', 'Alt klart. Klar for nye oppgaver!');
    } else if (activeCases.length <= 2 && todayMeetings.length === 0) {
      context = t('Light day. Good time to focus.', 'Rolig dag. Tid til å fokusere.');
    }
    
    return { greeting, context };
  }, [currentTime, user, allCases, todayTimeline, t]);

  // Streaming effect
  const streamBrief = useCallback(async () => {
    if (briefShownRef.current || briefShown) return;
    briefShownRef.current = true;
    
    const { greeting, context } = generateContextualGreeting();
    
    setIsStreaming(true);
    setStreamingText('');
    setStreamingContext('');
    setStreamingPhase('greeting');
    
    const cleanGreeting = greeting.replace(/\.$/, '');
    for (let i = 0; i <= cleanGreeting.length; i++) {
      await new Promise(resolve => setTimeout(resolve, Math.random() * 20 + 15));
      setStreamingText(cleanGreeting.slice(0, i));
    }
    
    if (context) {
      await new Promise(resolve => setTimeout(resolve, 300));
      setStreamingContext(context);
    }
    
    await new Promise(resolve => setTimeout(resolve, 400));
    setStreamingPhase('timeline');
    
    await new Promise(resolve => setTimeout(resolve, 300));
    setStreamingPhase('done');
    setIsStreaming(false);
    setBriefShown(true);
    
    addBriefMessage({
      type: 'brief',
      text: cleanGreeting,
      context: context,
      timestamp: new Date(),
      timeline: todayTimeline,
      suggestions: language === 'en' 
        ? ['What should I focus on?', 'Summarize my cases', 'Any urgent emails?']
        : ['Hva bør jeg fokusere på?', 'Oppsummer sakene mine', 'Noen hastesaker?']
    });
  }, [generateContextualGreeting, todayTimeline, language, briefShown, briefShownRef, setBriefShown, addBriefMessage]);

  useEffect(() => {
    if (briefShownRef.current) return;
    const timer = setTimeout(() => streamBrief(), 500);
    return () => clearTimeout(timer);
  }, [streamBrief, briefShownRef]);

  // Profile upload
  const handleProfileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/auth/profile-picture`, fd, { 
        headers: { 'Content-Type': 'multipart/form-data' } 
      });
      const u = { ...JSON.parse(localStorage.getItem('user') || '{}'), profile_picture_url: res.data.profile_picture_url };
      localStorage.setItem('user', JSON.stringify(u));
      window.dispatchEvent(new CustomEvent('userUpdated', { detail: u }));
      toast.success(t('Profile picture updated', 'Profilbilde oppdatert'));
      onUserUpdate?.();
    } catch { 
      toast.error(t('Could not upload', 'Kunne ikke laste opp')); 
    } finally { 
      setUploading(false); 
    }
  };

  // Create case
  const handleCreateCase = async (caseData: any) => {
    setIsCreatingCase(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/cases`, caseData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('Case created!', 'Sak opprettet!'));
      setShowNewCaseModal(false);
      if (response.data?.id) navigate(`/lexy/case/${response.data.id}/workspace`);
    } catch {
      toast.error(t('Could not create case', 'Kunne ikke opprette sak'));
    } finally {
      setIsCreatingCase(false);
    }
  };

  // Handle voice message - returns AI response for TTS
  const handleVoiceMessage = async (message: string): Promise<string> => {
    if (!message.trim()) return '';
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/ai/quick-question`, { 
        question: message.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      const answer = res.data.answer || t('I could not answer that.', 'Jeg kunne ikke svare på det.');
      
      // Also add to chat history for visual feedback
      handleSubmit(message);
      
      return answer;
    } catch {
      throw new Error(t('Something went wrong', 'Noe gikk galt'));
    }
  };

  // Quick actions
  const quickActions = [
    { icon: Plus, label: t('New case', 'Ny sak'), action: () => setShowNewCaseModal(true) },
    { icon: Calendar, label: t('Calendar', 'Kalender'), action: () => navigate('/lexy/calendar') },
    { icon: Clock, label: t('Time', 'Timer'), action: () => navigate('/lexy/time') },
  ];

  return (
    <div className="h-screen bg-white flex flex-col overflow-hidden">
      {/* Main content wrapper */}
      <div className="flex-1 flex overflow-hidden relative">
        
        {/* Chat area */}
        <div className={`flex flex-col overflow-hidden transition-all duration-300 relative ${
          documentSidePanel.isOpen ? 'w-[55%] min-w-[450px]' : 'w-full'
        }`}>
        
          {/* Search bar - hide when document panel is open */}
          {!documentSidePanel.isOpen && (
            <SearchDropdown
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              showDropdown={showSearchDropdown}
              setShowDropdown={setShowSearchDropdown}
              selectedIndex={searchSelectedIndex}
              setSelectedIndex={setSearchSelectedIndex}
              results={searchResults}
              onKeyDown={handleSearchKeyDown}
              onResultClick={handleSearchResultClick}
              inputRef={searchInputRef}
              dropdownRef={searchDropdownRef}
              t={t}
            />
          )}

          {/* Chat area - Full width scroll, content centered */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 scroll-smooth relative" ref={chatEndRef}>
            
            {/* Clean background - no decorations */}
            
            <div className="max-w-2xl mx-auto px-6 pb-4 relative z-10">
              
              {/* WORLD-CLASS Overview with elegant visual elements */}
              {!briefCollapsed && (isStreaming || briefShown || streamingText) && (
                <motion.div 
                  initial={{ opacity: 0 }} 
                  animate={{ opacity: 1 }} 
                  className="pt-16 sm:pt-20 relative"
                >
                  {/* Greeting with subtle gradient orb */}
                  <div className="mb-12 relative">
                    {/* Gradient orb - subtle AI presence */}
                    <div className="absolute -left-8 -top-8 w-32 h-32 bg-gradient-to-br from-violet-200/40 via-blue-200/30 to-transparent rounded-full blur-3xl pointer-events-none" />
                    
                    <div className="flex items-center gap-4 relative">
                      {user?.profile_picture_url ? (
                        <img 
                          src={`${user.profile_picture_url}?t=${Date.now()}`} 
                          alt="" 
                          className="w-14 h-14 rounded-full object-cover ring-2 ring-white shadow-lg" 
                        />
                      ) : (
                        <div className="w-14 h-14 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center text-white text-xl font-medium shadow-lg ring-2 ring-white">
                          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                      )}
                      <div>
                        <h1 className="text-3xl font-semibold text-gray-900 tracking-tight">
                          {streamingText || chatHistory[0]?.text?.split('.')[0] || t('Good afternoon', 'God ettermiddag')}
                          {streamingPhase === 'greeting' && isStreaming && (
                            <span className="inline-block w-0.5 h-7 bg-gray-900 ml-1 animate-pulse" />
                          )}
                        </h1>
                        {streamingContext && (
                          <p className="text-gray-500 mt-1 text-lg">
                            {streamingContext}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Floating card with gradient accent - only show when no chat history or just brief */}
                  {chatHistory.length <= 1 && (streamingPhase !== 'greeting' || briefShown) && (
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }} 
                      animate={{ opacity: 1, y: 0 }} 
                      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                      className="relative"
                    >
                      {/* Elegant floating card */}
                      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100/80 overflow-hidden">
                        {/* Gradient accent line at top */}
                        <div className="h-[2px] bg-gradient-to-r from-violet-500/60 via-blue-500/60 to-violet-500/60" />
                        
                        <div className="p-6">
                          {/* Section title */}
                          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-[0.15em] mb-5">
                            {t('Recommended', 'Anbefalt')}
                          </p>
                          
                          {/* Clean action list */}
                          <ActionPanel 
                            onNavigate={navigate} 
                            language={language}
                          />
                        </div>
                      </div>
                      
                      {/* Ultra-minimalist Stats Section */}
                      <div className="flex justify-center gap-12 mt-8 text-center">
                        <div>
                          <p className="text-2xl font-semibold text-gray-900">
                            {allCases?.filter((c: any) => c.status !== 'COMPLETED').length || 0}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {t('Active cases', 'Aktive saker')}
                          </p>
                        </div>
                        <div>
                          <p className="text-2xl font-semibold text-gray-900">
                            {todayTimeline?.filter((item: any) => item.type === 'deadline').length || 0}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {t('Deadlines today', 'Frister i dag')}
                          </p>
                        </div>
                        <div>
                          <p className="text-2xl font-semibold text-gray-900">
                            {todayTimeline?.filter((item: any) => item.type === 'meeting').length || 0}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {t('Meetings', 'Møter')}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              )}

              {/* Collapsed state - minimal header */}
              {briefCollapsed && briefShown && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="sticky top-0 z-10 bg-white pt-4 pb-3 border-b border-gray-100 mb-6"
                >
                  <button
                    onClick={() => setBriefCollapsed(false)}
                    className="flex items-center gap-3 text-left group"
                  >
                    <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm text-gray-500 group-hover:text-gray-900 transition-colors">
                      {t('Show overview', 'Vis oversikt')}
                    </span>
                  </button>
                </motion.div>
              )}

            {/* Chat history */}
            {chatHistory.map((msg, idx) => (
              <ChatMessage
                key={idx}
                msg={msg}
                idx={idx}
                user={user}
                briefCollapsed={briefCollapsed}
                setBriefCollapsed={setBriefCollapsed}
                chatHistoryLength={chatHistory.length}
                priorityCases={priorityCases}
                onNavigate={navigate}
                onSuggestionClick={handleSubmit}
                t={t}
                isStreaming={chatIsStreaming && idx === chatHistory.length - 1}
                onOpenDocumentSidePanel={(citation) => setDocumentSidePanel({ isOpen: true, citation })}
                language={language}
              />
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-2">
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 bg-gradient-to-br from-gray-800 to-black rounded-full flex items-center justify-center shrink-0 shadow-sm">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex items-center gap-1.5 pt-3">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </motion.div>
            )}
            
            <div ref={chatEndRef} />
          </div>
        </div>

        {/* Bottom section - World-class minimal input */}
        <div className="flex-shrink-0 bg-white max-w-2xl mx-auto w-full px-6">

          {/* Chat input - ChatGPT-style design */}
          <div className="relative flex items-center bg-white border border-gray-200 rounded-full shadow-sm transition-all duration-200 hover:shadow-md focus-within:shadow-md focus-within:border-gray-300">
            {/* Plus button - inside input */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="ml-2 w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
              title={t('Attach file', 'Legg ved fil')}
            >
              <Plus className="w-5 h-5" />
            </button>
            
            {/* Input field */}
            <input
              ref={inputRef}
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSubmit()}
              placeholder={t('Ask anything', 'Spør om hva som helst')}
              className="flex-1 bg-transparent py-3.5 px-3 text-gray-900 placeholder-gray-400 focus:outline-none text-[15px] border-none ring-0 focus:ring-0"
              style={{ outline: 'none', boxShadow: 'none' }}
            />
            
            <div className="flex items-center gap-1 pr-2">
              {/* Voice Mode */}
              <button
                onClick={() => setVoiceModeOpen(true)}
                className="w-9 h-9 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                title={t('Voice message', 'Talemelding')}
              >
                <Mic className="w-[18px] h-[18px]" />
              </button>
              
              {/* Send button - ChatGPT style cyan/teal */}
              <button
                onClick={() => handleSubmit()}
                disabled={!chatInput.trim() || isTyping}
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-all duration-200 ${
                  chatInput.trim() && !isTyping
                    ? 'bg-[#10a37f] text-white hover:bg-[#0d8c6d]'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isTyping ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
          
          {/* Subtle footer text */}
          <p className="text-center text-[11px] text-gray-400 mt-4 mb-6">
            {t('Lexy can analyze all your documents and cases', 'Lexy kan analysere alle dine dokumenter og saker')}
          </p>
        </div>
        </div>
        
        {/* 🎯 Saga-style PDF Side Panel - Inline in layout */}
        <PDFSidePanel
          isOpen={documentSidePanel.isOpen}
          onClose={() => setDocumentSidePanel({ isOpen: false, citation: undefined })}
          citation={documentSidePanel.citation}
          inline={true}
        />
      </div>

      <CreateCaseModal
        isOpen={showNewCaseModal}
        onClose={() => setShowNewCaseModal(false)}
        onConfirm={handleCreateCase}
        isCreating={isCreatingCase}
        prefillData={{}}
        brief={null}
        attachments={[]}
      />

      {/* Voice Mode Overlay */}
      <VoiceMode
        isOpen={voiceModeOpen}
        onClose={() => setVoiceModeOpen(false)}
        onSendMessage={handleVoiceMessage}
        language={language === 'en' ? 'en' : 'nb'}
      />
    </div>
  );
};

export default OverviewViewMinimal;
